import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AudienceBadge, ReviewBadge, StatusBadge } from "@/components/resources/ResourceBadges";
import { ResourcePreview } from "@/components/resources/ResourcePreview";
import { ResourceMetaCard } from "@/components/resources/ResourceMetaCard";
import { ResourceActions, downloadVersion } from "@/components/resources/ResourceActions";
import { ApprovalHistory, AuditList, UsageHistory, VersionHistory } from "@/components/resources/ResourceHistory";
import { errText, useResourceMeta } from "@/components/resources/resourceApi";

function StateBanner({ res, previewVersion }) {
  if (res.status === "archived") {
    return <div className="rounded-lg border border-zinc-300 bg-zinc-100 px-4 py-2.5 text-sm text-zinc-700" data-testid="resource-archived-banner">
      Archived by {res.archived_by_name}: {res.archive_reason}. It can't be sent to clients; everything already issued is kept.</div>;
  }
  if (previewVersion && previewVersion.status !== "approved") {
    return <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="resource-unapproved-banner">
      <AlertTriangle className="h-4 w-4" /> You're viewing v{previewVersion.version_number} ({previewVersion.status.replace("_", " ")}) — not approved for client use.</div>;
  }
  return null;
}

export default function ResourceDetail() {
  const { id } = useParams();
  const meta = useResourceMeta();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState(null);
  const [audit, setAudit] = useState(null);

  const load = useCallback(() => {
    api.get(`/clinical-resources/${id}`).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errText(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="text-sm text-red-700" data-testid="resource-detail-error">{error}</div>;
  if (!data || !meta) return <div className="text-sm text-zinc-500"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div>;
  const res = data.resource;
  const pv = (selected && data.versions.find((v) => v.id === selected)) || data.versions.find((v) => v.id === res.current_version_id) || data.versions[0];
  const loadAudit = () => api.get("/clinical-resources/audit", { params: { resource_id: id } }).then((r) => setAudit(r.data.items)).catch(() => setAudit([]));

  return (
    <div className="space-y-5" data-testid="resource-detail-page">
      <Link to={`/resource-library${res.audience === "practitioner" ? "?audience=practitioner" : ""}`} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid="resource-back-link">
        <ArrowLeft className="h-3.5 w-3.5" /> Clinical Resource Library</Link>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={res.status} testId="resource-detail-status" /><AudienceBadge audience={res.audience} />
          {res.current_version_number && <span className="font-mono text-xs text-zinc-500" data-testid="resource-detail-version">current v{res.current_version_number}</span>}
          <ReviewBadge state={res.review_state} />
        </div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-950" data-testid="resource-detail-title">{res.title}</h1>
      </div>
      <ResourceActions data={data} meta={meta} onChanged={() => { load(); setAudit(null); }} decision={decision} setDecision={setDecision} />
      <StateBanner res={res} previewVersion={pv} />
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-4">
          {pv?.file ? <ResourcePreview key={pv.id} path={`/clinical-resources/versions/${pv.id}/file`} file={pv.file} />
            : <Card className="p-6 text-sm text-zinc-500 shadow-none">{data.versions.length ? "This resource links to another module — see Linked modules." : "No file uploaded yet — use Upload new version."}</Card>}
          <Card className="rounded-lg border border-zinc-200 p-4 shadow-none">
            <Tabs defaultValue="versions" onValueChange={(v) => v === "audit" && !audit && loadAudit()}>
              <TabsList>
                <TabsTrigger value="versions" data-testid="resource-tab-versions">Versions ({data.versions.length})</TabsTrigger>
                {!meta.perms.reception && <TabsTrigger value="reviews" data-testid="resource-tab-reviews">Reviews</TabsTrigger>}
                <TabsTrigger value="usage" data-testid="resource-tab-usage">Usage ({data.usage_count})</TabsTrigger>
                {meta.perms.audit && <TabsTrigger value="audit" data-testid="resource-tab-audit">Audit log</TabsTrigger>}
              </TabsList>
              <TabsContent value="versions" className="pt-2">
                <VersionHistory versions={data.versions} selectedId={pv?.id} onSelect={(v) => setSelected(v.id)} onDownload={downloadVersion}
                                canApprove={meta.perms.approve} onDecide={(version, kind) => setDecision({ version, kind })} />
              </TabsContent>
              <TabsContent value="reviews" className="pt-2"><ApprovalHistory approvals={data.approvals} /></TabsContent>
              <TabsContent value="usage" className="pt-2"><UsageHistory usage={data.usage} count={data.usage_count} /></TabsContent>
              <TabsContent value="audit" className="pt-2">{audit ? <AuditList items={audit} /> : <Loader2 className="h-4 w-4 animate-spin" />}</TabsContent>
            </Tabs>
          </Card>
        </div>
        <ResourceMetaCard res={res} meta={meta} />
      </div>
    </div>
  );
}
